import { utils, LitElement, html, css, unsafeHTML, paths, t } from '@hh/global.js'
const { one, reRenderShopifySections, addToCart, updateCartItem } = utils;

// ============================================================================
// Fast Bundle API Adapter Helpers
// ============================================================================

const FB_API_BASE = 'https://api.fastbundle.co';

/**
 * Fetch Bundle as Product (BAP) from Fast Bundle API
 */
async function fetchBAP({ shop, product_id, lang, is_preview, url }) {
  const params = new URLSearchParams({
    shop,
    url: url || '/',
    is_preview: is_preview ? 'true' : 'false',
    lang: lang || 'en',
  });
  
  const endpoint = `${FB_API_BASE}/v3/baps/${product_id}/?${params}`;
  
  try {
    const response = await fetch(endpoint);
    
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data;
  } catch (e) {
    return null;
  }
}

/**
 * Fetch collection products from Fast Bundle API
 */
async function fetchCollectionProducts({ shop, collection_id, lang, page = 1 }) {
  const params = new URLSearchParams({
    shop_domain: shop,
    collection_id: String(collection_id),
    page: String(page),
    lang: lang || 'en'
  });
  
  const endpoint = `${FB_API_BASE}/v3/collection-products/?${params}`;
  
  try {
    const response = await fetch(endpoint);
    
    if (!response.ok) {
      return [];
    }
    
    const data = await response.json();
    return data.results || [];
  } catch (e) {
    return { results: [] };
  }
}
/**
 * Get Shopify cart token
 */
async function getCartToken() {
  try {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    const token = cart.token || '';
    return token;
  } catch (e) {
    return '';
  }
}

/**
 * Hash helper for UID generation (simple hash without secret)
 */
function generateHash(...parts) {
  const combined = parts.map(p => String(p || 0)).join('');
  return combined.split('').reverse().join('');
}

/**
 * Normalize BAP bundle data with collections to existing UI shape
 */
async function normalizeBAPBundle(bapData, legacyData, ctx) {
  
  if (!bapData || !bapData.collection_items) {
    return null;
  }

  
  // Determine bundle type and value from FB discounts
  let type = 0; // 0=fixed price, 1=percentage, 2=fixed amount
  let value = legacyData?.value || 0;
  
  const discount = bapData.discounts[0];
  
  if (discount.type === 'PER') {
    type = 1;
    value = discount.value || 0;
  } else if (discount.type === 'FIX') {
    type = 2;
    value = discount.value || 0;
  } else if (discount.type === 'SET') {
    type = 0;
    value = discount.value || 0;
  }
  
  // Build steps from collection_items
  const steps = [];
  
  for (const collectionItem of bapData.collection_items) {
    
    // Fetch products for this collection
    const collectionProducts = await fetchCollectionProducts({
      shop: ctx.shop,
      collection_id: collectionItem.collection.id,
      lang: ctx.locale
    });
    
    // Convert collection products to our product format
    const products = [];
    
    collectionProducts.forEach(product => {
      const variants = product.variants || [];
      
      variants.forEach(variant => {
        const priceCents = Math.round((variant.raw_price || 0) * 100);
        const price = variant.price || (priceCents / 100).toFixed(2);
        
        let sale_price = null;
        if (variant.raw_compare_at_price && variant.raw_compare_at_price > variant.raw_price) {
          const salePriceCents = Math.round((variant.raw_price || 0) * 100);
          sale_price = (salePriceCents / 100).toFixed(2);
        }
        
        const variantId = variant.id;
        const productHash = generateHash(variantId, collectionItem.id, type);
        
        products.push({
          id: String(product.product_id || ''),
          uid: productHash,
          variantId: variantId,
          title: product.title || '',
          handle: product.handle || '',
          badge: '',
          annotation: product.description?.substring(0, 150) || '',
          description: product.description || '',
          content: '',
          info: '',
          image: variant.image || product.image || '',
          images: [product.image, variant.image].filter(Boolean),
          ages: '',
          dimensions: '',
          pieces: '',
          price: price,
          priceCents: priceCents,
          sale_price: sale_price,
          quantity: '1',
          catalog_number: '',
          stockQty: variant.quantity || 0,
          available: (variant.quantity || 0) > 0,
          _fbItemId: String(product.item_id || collectionItem.id),
          _fbCollectionItemId: collectionItem.id,
          _fbProductId: product.product_id
        });
      });
    });
    
    const step = {
      title: collectionItem.section_title || collectionItem.collection.title || 'Select Products',
      quantity: String(collectionItem.quantity || 1),
      description: collectionItem.section_description || '',
      products: products,
      _fbCollectionId: collectionItem.collection.id,
      _fbCollectionItemId: collectionItem.id
    };
    
    steps.push(step);
  }
  
  // Generate bundle UID
  const bundleHash = generateHash(
    legacyData?.variant_id || bapData.bap_product_id || 0,
    type,
    steps.length,
    value
  );
  
  const normalized = {
    customerId: legacyData?.customerId || null,
    savedBundles: legacyData?.savedBundles || [],
    type: type,
    uid: bundleHash,
    discount_message: bapData.discounts.length > 0 ? `Spare ${value}%` : '',
    packaging_included: legacyData?.packaging_included || false,
    value: value,
    variant_id: legacyData?.variant_id || bapData.bap_product_id || 0,
    product_title: legacyData?.product_title || bapData.title || bapData.name || '',
    product_handle: legacyData?.product_handle || '',
    box_image: legacyData?.box_image || '',
    steps: steps,
    _fbBundle: {
      id: bapData.id,
      uuid: bapData.uuid,
      bap_product_id: bapData.bap_product_id
    }
  };
  
  return normalized;
}

class HHBundle extends LitElement {
  static properties = {
    _data: {
      state: true,
    },
    _bundle: {
      state: true,
    },
    loading: {
      state: true,
    },
    _currentStep: {
      state: true,
    },
    _products: {
      state: true,
    },
    _previewProduct: {
      state: true,
    },
    _lineItemId: {
      state: true,
    },
    _savedBundlesCount: {
      state: true,
    },
  }

  constructor() {
    super();
    this._previewProduct = null;
    this.loading = false;
    document.addEventListener('customer:login', () => {
      // close login modal, save bundle
      document.getElementById('login-dialog').querySelector('.main-bundle__backdrop').click();
      this.saveBundle();
    });
  }

  async firstUpdated() {
    
    const params = new URLSearchParams(window.location.search);
    const legacyData = JSON.parse(one('script', this).textContent);
    
    
    // Try to fetch Fast Bundle data
    let data = legacyData;
    const ctx = window.__bundleContext || {};
    
    
    if (ctx.shop && ctx.product_id) {
      
      try {
        // Primary method: Fetch BAP (Bundle as Product)
        const bapData = await fetchBAP({
          shop: ctx.shop,
          product_id: ctx.product_id,
          lang: ctx.locale,
          is_preview: ctx.is_preview,
          url: ctx.url
        });
        
        if (bapData) {
          const normalized = await normalizeBAPBundle(bapData, legacyData, ctx);
          if (normalized) {
            data = normalized;
          } else {  
          }
        } else {
        }
      } catch (e) {
      }
    } else {
    }
    
    this._bundle = data.steps.map((step) => ([]));
    this._currentStep = +params.get('s') || 0;
    this._savedBundlesCount = data.savedBundles?.length || 0;

    if (this._currentStep >= data.steps.length) {
      this._currentStep = 0;
    }

    const bundleData = params.get('b');
    if (bundleData) {
      const step = params.get('s');
      this._currentStep = step ? parseInt(step, 10) : 0;
      
      const bundle = data.steps.map((step) => ([]));
      bundleData.split('|').map((step) => {
        return step.split(',').map((item,) => {
          const [variantId, qty] = item.split(':');
          if (!variantId) {
            return null;
          }
          return {
            variantId: parseInt(variantId, 10),
            qty: parseInt(qty, 10),
          };
        }).filter((item) => item);
      }).forEach((step, index) => {
        step.forEach((item) => {
          if (
            data.steps[index]
            && data.steps[index].products.find((product) => product.variantId === item.variantId)
          ) {
            bundle[index].push(item);
          }
        });
      });
      this._bundle = [...bundle];
    }
    this._data = data;
    if (params.get('id')) {
      this._lineItemId = params.get('id');
      console.log('🔧 firstUpdated: Set _lineItemId from URL:', this._lineItemId);
    } else {
      console.log('🔧 firstUpdated: No ?id= in URL, _lineItemId remains null');
    }
    if (params.get('save')) {
      this.saveBundle();
    }
  }

  updateBundle(event) {
    if (event.detail.qty < 1) {
      const bundle = [...this._bundle];
      bundle[this._currentStep] = this._bundle[this._currentStep].filter((item) => item.variantId !== event.detail.variantId);
      this._bundle = [...bundle];
    } else {
      const addedItemIndex = this._bundle[this._currentStep].findIndex((item) => item.variantId === event.detail.variantId);
      const addedItem = this._bundle[this._currentStep][addedItemIndex];
      const bundle = [...this._bundle];
      let qty = event.detail.qty;
      const currentQty = bundle[this._currentStep].reduce(
        (acc, item) => {
          if (item.variantId !== event.detail.variantId) {
            acc += item.qty;
          }
          return acc;
        }, 0
      );
      const maxQty = this._data.steps[this._currentStep].quantity - currentQty;

      if (qty > maxQty) {
        qty = maxQty;
      }

      if (qty < 1) {
        return;
      }

      if (addedItem) {
        bundle[this._currentStep][addedItemIndex] = {
          variantId: event.detail.variantId,
          qty,
        };
      } else{
        bundle[this._currentStep].push({
          variantId: event.detail.variantId,
          qty
        });
      }
      this._bundle = [...bundle];
    }

    this.updateLocation();
  }

  formatParams() {
    return this._bundle.map((items, index) => {
      return items.map((item) => `${item.variantId}:${item.qty}`).join(',');
    }).join('|');
  }

  updateLocation() {
    const newUrl = `${window.location.pathname}?b=${this.formatParams()}&s=${this._currentStep}${this._lineItemId ? `&id=${this._lineItemId}` : ''}`;
    console.log('📍 updateLocation called');
    console.log('  Current URL:', window.location.href);
    console.log('  New URL:', newUrl);
    console.log('  _lineItemId:', this._lineItemId);
    window.history.replaceState(
      null,
      '',
      newUrl
    );
  }

  openLoginModal(e) {
    e?.preventDefault();
    e?.stopPropagation();
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'return_to';
    input.value = `${window.location.pathname}?b=${this.formatParams()}&s=${this._currentStep}&save=1`;
    document.getElementById('customer_login')?.appendChild(input);
    this.dispatchEvent(
      new CustomEvent('openModal', {
        bubbles: true,
        composed: true,
        detail: {
          id: 'login-dialog'
        },
      })
    )
  }

  openShareModal(e) {
    e.preventDefault();
    e.stopPropagation();
    document.dispatchEvent(new CustomEvent('updateShareUrl'));
    this.dispatchEvent(
      new CustomEvent('openModal', {
        bubbles: true,
        composed: true,
        detail: {
          id: 'share-dialog'
        },
      })
    )
  }

  saveBundle() {
    this.loading = true;

    return fetch('/?view=account').then((response) => response.json()).then((data) => {
      if (data.id) {
        const url = `${window.location.pathname}?b=${this.formatParams()}&s=${this._currentStep}`;
        this._data.customerId = data.id;
        const bundles = data.saved_bundles || [];
        const savedBundleIndex = bundles.findIndex((bundle) => bundle.handle === this._data.product_handle);
        const payload = {
          finished: this._completedSteps.filter(i => i).length,
          total: this._data.steps.length,
          handle: this._data.product_handle,
          url,
        }
        if (savedBundleIndex >= 0)  {
          bundles[savedBundleIndex] = payload
        } else if (bundles.length < 10) {
          bundles.push(payload);
        } else {
          return;
        }
        this._savedBundlesCount = bundles.length;
        window.history.replaceState(
          null,
          '',
          url
        );
        return fetch("https://" + window.__bundleContext.shop + "/apps/tools/api/account", {
          "headers": {
            "content-type": "application/x-www-form-urlencoded",
          },
          "body": new URLSearchParams({
            'customer[id]': data.id,
            'metafields[hhschleich.saved_bundles.json]': JSON.stringify(bundles)
          }).toString(),
          "method": "POST",
        });
      } else {
        this.openLoginModal()
      }
    }).finally(() => {
      this.loading = false;
    });
  }

  onSaveBundle(event) {
    if (this._data.customerId) {
      this.saveBundle();
    } else {
      this.openLoginModal(event);
    }
  }

  changeStep(step) {
    if (step >= this._data.steps.length) {
      this.addBundleToCart();
      return;
    }
    this._currentStep = step;
    this.updateLocation();
  }

  async addBundleToCart() {
    this.loading = true;
    const ctx = window.__bundleContext || {};

    if (this._data._fbBundle) {
      const params = new URLSearchParams(window.location.search);
      const isEditing = !!this._lineItemId && !!params.get('id');
      
      try {
        if (isEditing) {
          const currentCart = await fetch('/cart.js').then(r => r.json());
          const oldBundleId = currentCart.items.find(item => item.key === this._lineItemId)?.properties?.__cartBundleId;
          
          if (oldBundleId) {
            const oldBundleItems = currentCart.items.filter(item => 
              item.properties?.__cartBundleId === oldBundleId
            );
            
            for (const oldItem of oldBundleItems) {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  id: oldItem.key,
                  quantity: 0
                })
              });
            }
          }
          
          this._lineItemId = null;
        } 
        
        const token = await getCartToken();
        if (!token) {
          throw new Error('Cart token not available');
        }

        const bundle_variants = [];
        const bapData = this._data._fbBundle;
        
        this._bundle.forEach((step, stepIndex) => {
          step.forEach((item) => {
            const product = this._data.steps[stepIndex].products.find(
              (p) => p.variantId === item.variantId
            );
            if (product && product._fbCollectionItemId) {
              // Need to get collection_id from the BAP data
              const stepData = this._data.steps[stepIndex];
              const collectionId = stepData._fbCollectionId || null;
              for (let i = 0; i < item.qty; i++) {
                bundle_variants.push({
                  variant_id: item.variantId,
                  collection_item_id: String(product._fbCollectionItemId),
                  collection_id: collectionId
                });
              }
            }
          });
        });

        if (bundle_variants.length === 0) {
          throw new Error('No valid bundle variants');
        }

        const payload = {
          token,
          bundle: bapData.id,
          bundle_variants,
          show_uuid: bapData.uuid || '',
          shop: ctx.shop,
          user_ip: '',
          click_from_url: ctx.url || window.location.pathname,
          click_from_page_type: 'product',
          is_single_bap: false
        };

        const response = await fetch(`${FB_API_BASE}/v1/carts/bundles/`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'fb-shop-domain': ctx.shop,
            'x-fastbundle-request': 'yes'
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Fast Bundle API error: ${response.status}`);
        }

        const fbResult = await response.json();
        
        const cartBundleId = String(fbResult.id);
        const sellingPlanId = fbResult.selling_plan_id || null;

        const bundleItems = this._bundle.reduce((acc, step, index) => {
          const products = [];
          step.forEach((item) => {
            const product = this._data.steps[index].products.find((product) => product.variantId === item.variantId);
            if (product) {
              products.push({
                id: item.variantId,
                qty: item.qty,
                uid: product.uid,
                pr: product.priceCents,
              });
            }
          });
          return [...acc, ...products];
        }, []);
        
        const queryString = this.formatParams();
        
        const bundleProperties = {
          _bundle: JSON.stringify(bundleItems),
          _query: queryString,
          _uid: this._data.uid,
          _bundleValue: this._data.value,
          _bundleType: this._data.type,
          __cartBundleId: cartBundleId
        };
        
        const cartItems = [];
        
        this._bundle.forEach((step, stepIndex) => {
          step.forEach((item) => {
            const cartItem = {
              id: item.variantId,
              quantity: item.qty,
              properties: { ...bundleProperties }
            };
            
            if (sellingPlanId) {
              cartItem.selling_plan = sellingPlanId;
            }
            
            cartItems.push(cartItem);
          });
        });
        
        await addToCart({ items: cartItems });

        await this.getFastBundleDiscountCode(cartBundleId, sellingPlanId !== null).then(async () => {
          await new Promise(resolve => setTimeout(resolve, 800));
        
          // Step 5: Fetch fresh cart with sections HTML
          const sections = ['cart-notification', 'cart-icon-bubble', 'cart-drawer', 'mini-cart'];
          const sectionsParam = sections.map(section => `sections=${section}`).join('&');
          
          const cartUpdateResponse = await fetch(`/cart/update.js?${sectionsParam}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: {} })
          });
          
          if (!cartUpdateResponse.ok) {
            throw new Error('Failed to fetch cart sections');
          }
          
          const freshCartData = await cartUpdateResponse.json();
          
          await reRenderShopifySections(freshCartData);
        }).catch(error => {
         this.loading = false;
        alert('Failed to apply discount. Please try again.');
        });

      } catch (e) {
        this.loading = false;
        alert('Failed to add bundle to cart. Please try again.');
        return;
      }
    } else {
      
      const items = this._bundle.reduce((acc, step, index) => {
        const products = [];
        step.forEach((item) => {
          const product = this._data.steps[index].products.find((product) => product.variantId === item.variantId);
          if (product) {
            products.push({
              id: item.variantId,
              qty: item.qty,
              uid: product.uid,
              pr: product.priceCents,
            });
          }
        });
        return [...acc, ...products];
      }, []);
      
      const properties = {
        _bundle: JSON.stringify(items),
        _query: this.formatParams(),
        _uid: this._data.uid,
        _bundleValue: this._data.value,
        _bundleType: this._data.type,
      };
      
      const data = {
        items: [
          {
            id: this._data.variant_id,
            qty: 1,
            properties
          }
        ]
      };
      
      const params = new URLSearchParams(window.location.search);
      const isEditingLegacy = !!this._lineItemId && !!params.get('id');
      
      if (isEditingLegacy) {
        await reRenderShopifySections(
          await updateCartItem({
          id: this._lineItemId,
          properties,
        }));
        this._lineItemId = null;
      } else {
        await reRenderShopifySections(await addToCart(data));
      }
    }

    this.loading = false;
    this._bundle = this._data.steps.map((step) => ([]));
    this._currentStep = 0;

    fetch('/?view=account').then((response) => response.json()).then((data) => {
      const bundles = data.saved_bundles || [];
      const savedBundleIndex = bundles.findIndex((bundle) => bundle.handle === this._data.product_handle);
      if (savedBundleIndex >= 0) {
        bundles.splice(savedBundleIndex, 1);
        this._savedBundlesCount = bundles.length;
        return fetch("https://" + window.__bundleContext.shop + "/apps/tools/api/account", {
          "headers": {
            "content-type": "application/x-www-form-urlencoded",
          },
          "body": new URLSearchParams({
            'customer[id]': data.id,
            'metafields[hhschleich.saved_bundles.json]': JSON.stringify(bundles)
          }).toString(),
          "method": "POST",
        });
      }
    });

    this.updateLocation();
    
    // Dispatch notifications
    this.dispatchEvent(
      new CustomEvent('productAdded', {
        bubbles: true,
        composed: true,
        detail: {
          id: 'cart-notification',
          product_title: this._data.product_title,
          auto_close: 2500,
        },
      })
    )
    window.dispatchEvent(
      new CustomEvent('cart-message:added', {
        bubbles: true,
        composed: true,
        detail: {
          title: this._data.product_title,
          link: `/products/${this._data.product_handle}`,
        },
      })
    );
  }

  /**
   * Step 3: Trigger Fast Bundle discount calculation
   * Calls /v1/carts/discounts/ to apply discount (cart transform or discount code)
   * @returns {Object|null} Discount result with code (if applicable) or isCartTransform flag
   */
  async getFastBundleDiscountCode(cartBundleId, hasSellingPlan = false) {
    const ctx = window.__bundleContext || {};
  
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const token = cart.token;
      if (!token) return null;
  
      // ---------------------------
      // BUILD VARIANTS MAP
      // ---------------------------
      const variants = {};
  
      cart.items.forEach(item => {
        const variantId = String(item.variant_id);
        const bundleId = item.properties?.__cartBundleId;
  
        if (!variants[variantId]) {
          variants[variantId] = {
            product_id: item.product_id,
            quantity: 0,
            price: null,
            compare_at_price: null
          };
        }
  
        // суммарное количество
        variants[variantId].quantity += item.quantity;
  
        // если это бандл — учитываем
        if (bundleId) {
          if (!variants[variantId].cart_bundle_ids) {
            variants[variantId].cart_bundle_ids = {};
          }
  
          variants[variantId].cart_bundle_ids[bundleId] =
            (variants[variantId].cart_bundle_ids[bundleId] || 0) + item.quantity;
        }
      });
  
      const discountPayload = {
        shop: ctx.shop,
        user_ip: '',
        variants,
        token,
        has_selling_plan: hasSellingPlan
      };
  
      const response = await fetch(`${FB_API_BASE}/v1/carts/discounts/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(discountPayload)
      });
  
      if (!response.ok) return null;
  
      const result = await response.json();
  
      if (result.valid_ct_cart_bundle_ids?.length > 0) {
        return { isCartTransform: true, ...result };
      }
  
      if (result.code) {
        return { code: result.code, ...result };
      }
  
      return null;
    } catch (err) {
      return null;
    }
  }
  
  
  
  /**
   * Step 4: Apply discount code to Shopify cart
   */
  async applyDiscountCodeToCart(discountCode) {
    try {
      
      const response = await fetch(`/discount/${encodeURIComponent(discountCode)}`, {
        method: 'GET'
      });
      
      
      if (!response.ok) {
        return false;
      }
      
      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            discount_code: discountCode
          }
        })
      });
      
      if (updateResponse.ok) {
        const cart = await updateResponse.json();
        return true;
      } else {
        return false;
      }
      
    } catch (err) {
      return false;
    }
  }

  get _completedSteps() {
    return this._bundle.map((items, index) => {
      return items.reduce((acc, item) => acc + item.qty, 0) >= this._data.steps[index].quantity;
    });
  }

  get isCurrentStepFinished() {
    return this._completedSteps[this._currentStep];
  }

  get isBundleFinished() {
    return this._completedSteps.every(i => i);
  }

  get currentItemsCount() {
    return this._bundle[this._currentStep].reduce((acc, item) => acc + item.qty, 0);
  }

  render() {
    if (!this._data) return html`<div></div>`;
    const currentStep = this._data.steps[this._currentStep];
    const title = t('sections.main-bundle.title').replace('{{ step }}', this._currentStep + 1).replace('{{ count }}', currentStep.quantity).replace('{{ title }}', currentStep.title);
    return html`
    <div class="bundle">
      <hh-bundle-summary
        .currentStep="${this._currentStep}"
        .steps="${this._data.steps}"
        .bundle="${this._bundle}"
        .message="${this._data.discount_message}"
        .currentItemsCount="${this.currentItemsCount}"
        .packagingIncluded="${this._data.packaging_included}"
        .boxImage="${this._data.box_image}"
        @changeStep="${(e) => this.changeStep(e.detail.step)}"
        class="bundle__summary"
      >
        <hh-bundle-buttons
          direction="column"
          .currentStep="${this._currentStep}"
          .isCurrentStepFinished="${this.isCurrentStepFinished}"
          .isBundleFinished="${this.isBundleFinished}"
          .stepsCount="${this._data.steps.length}"
          .isLoading="${this.loading}"
          @onPrevStep="${() => this.changeStep(this._currentStep - 1)}"
          @onNextStep="${() => this.changeStep(this._currentStep + 1)}"
        ></hh-bundle-buttons>
      </hh-bundle-summary>
      <div>
        ${this._previewProduct ? html`<hh-bundle-modal
          .product="${this._previewProduct}"
          .bundle="${this._bundle}"
          .currentStep="${this._currentStep}"
          .maxStepQty="${currentStep.quantity}"
          @close="${() => this._previewProduct = null}"
          @updateBundle="${this.updateBundle}"
        ></hh-bundle-modal>` : ''}
        <div class="bundle__title">
          <div class="bundle__title-text">
            ${t('sections.main-bundle.step_of').replace('{{ number }}', this._currentStep + 1).replace('{{ total }}', this._data.steps.length)}:
            ${title}
          </div>
          <div class="bundle__progress">
            <div class="bundle__progress-bar">
              <div class="bundle__progress-bg" style="width: ${this.currentItemsCount/currentStep.quantity * 100}%"></div>
            </div>
            <div class="bundle__progress-text">
              ${this.currentItemsCount}/${currentStep.quantity} ${t('sections.main-bundle.selected')}
            </div>
          </div>
        </div>
        ${currentStep.description ? html`<div class="bundle-step__description">${currentStep.description}</div>` : ''}
        <div class="bundle__save">
          <hh-button-wrapper theme='secondary'>
            <button class="bundle__save-button" @click="${this.openShareModal}">
              ${t('sections.main-bundle.share')}
            </button>
           </hh-button-wrapper>
        </div>
        <hh-bundle-step
          @updateBundle="${this.updateBundle}"
          @previewProduct="${(e) => this._previewProduct = {...e.detail.product}}"
          .step="${currentStep}"
          .bundle="${this._bundle}"
          .currentStep="${this._currentStep}"
          .isFinished="${this.isCurrentStepFinished}"
          .type="${this._data.type}"
        >
        </hh-bundle-step>
        <hh-bundle-buttons
          class="bundle__buttons"
          direction="row"
          .currentStep="${this._currentStep}"
          .isCurrentStepFinished="${this.isCurrentStepFinished}"
          .isBundleFinished="${this.isBundleFinished}"
          .stepsCount="${this._data.steps.length}"
          .isLoading="${this.loading}"
          @onPrevStep="${() => this.changeStep(this._currentStep - 1)}"
          @onNextStep="${() => this.changeStep(this._currentStep + 1)}"
        ></hh-bundle-buttons>
      </div>
    </div>
    `
  }

  static styles = css`
    hh-button-wrapper {
      --hh-background: var(--hh-color-brand-secondary);
      --hh-background-hover: transparent;
      --hh-color-disabled: var(--hh-color-light);
      --hh-background-disabled: var(--hh-color-subdued);
      --hh-font: var(--hh-button-large-font);
      --hh-letter-spacing: var(--hh-button-letter-spacing);
      --hh-padding: var(--hh-spacing-xs) var(--hh-spacing-2xl);
      --hh-height: auto;
      --hh-max-height: auto;
      --hh-max-width: 22rem;
      --hh-text-transform: uppercase;
      --hh-transition: all var(--hh-duration-default) ease;
      --hh-border-bottom-offset: -1px;
      --hh-border-radius: 9999px;
    }

    hh-button-wrapper[theme='secondary'] {
      --hh-color: var(--hh-color-brand-secondary);
      --hh-background: transparent;
      --hh-border-color: var(--hh-color-brand-secondary);
      --hh-color-hover: var(--hh-color-day);
      --hh-background-hover: var(--hh-color-brand-secondary);
      --hh-color-disabled: var(--hh-color-subdued);
      --hh-background-disabled: transparent;
      --hh-border-disabled: var(--hh-color-subdued);
    }

    .bundle {
      padding-top: var(--hh-spacing-xl);
      position: relative;
    }

    .bundle__title {
      font: var(--hh-h2-font);
      letter-spacing: var(--hh-h2-letter-spacing);
      margin-bottom: var(--hh-spacing-sm);
      color: var(--hh-color-night);
    }

    .bundle__buttons {
      display: none;
    }

    .bundle__progress {
      display: none;
    }

    .bundle__progress-bar {
      width: 100%;
      height: var(--hh-spacing-sm);
      border-radius: 6.6rem;
      border: 0.1rem solid rgba(var(--hh-color-night-rgb), 0.22);
      overflow: hidden;
    }

    .bundle__progress-bg {
      height: 100%;
      background: var(--hh-color-orange);
    }

    .bundle-step__description {
      font: var(--hh-body-sm-font);
      margin-bottom: var(--hh-spacing-xl);
    }

    .bundle__save {
      display: flex;
      gap: var(--hh-spacing-2xl);
      margin-bottom: var(--hh-spacing-xl);
    }

    .bundle__save-button:hover:not(:disabled) {
      background-color: var(--hh-background-hover, var(--hh-color-background));
      color: var(--hh-color-hover, var(--hh-background));
    }

    @media (min-width: 1024px) {
      .bundle {
        display: flex;
        flex-direction: row-reverse;
        gap: var(--hh-spacing-3xl);
        align-items: flex-start;
      }
      .bundle hh-bundle-summary {
        position: sticky;
        top: 0;
      }
      .bundle > * {
        flex: 1;
      }
      .bundle__summary {
        max-width: 43rem;
      }
      .bundle__buttons{
        margin-top: var(--hh-spacing-xl);
        padding: var(--hh-spacing-xl) 0;
        display: flex;
        justify-content: center;
      }
      .bundle__title{
        padding-top: var(--hh-spacing-xl);
        margin-bottom: var(--hh-spacing-xl);
        display: flex;
        gap: var(--hh-spacing-sm);
        align-items: flex-end;
      }
      .bundle__title-text{
        flex: 1;
      }
      .bundle__progress{
        display: flex;
        gap: var(--hh-spacing-xs);
        align-items: center;
      }
      .bundle__progress-bar{
        width: 8rem;
      }
      .bundle__progress-text {
        font: var(--hh-body-bold-lg-font);
      }
    }
  `
}

if (!customElements.get('hh-bundle')) {
  customElements.define('hh-bundle', HHBundle);
}
